import type { AxiosResponse } from 'axios';
import api from '../../redux/features/auth/axiosAuthHelper';
import { apiError } from '../../services/apiError';
import { downloadFilename } from '../../services/tableExport';
import { validDefaults, type WorkflowDefaults } from './accountApi';

export const IMPORT_ENDPOINT = 'customer-account-imports/';
export const IMPORT_COLUMNS = [
  'account_number',
  'account_name',
  'alcohol_workflow_enabled',
  'alcohol_send_to_lab_enabled',
  'manual_override_enabled',
];
export interface ImportSummary {
  id: string;
  filename: string;
  actor_email: string;
  created_at: string;
  completed_at: string | null;
  status: 'ready' | 'completed' | 'failed';
  error: string;
  defaults: WorkflowDefaults;
  total: number;
  ready: number;
  created: number;
  skipped: number;
  rejected: number;
  defaulted: number;
}
export interface ImportRow {
  row_number: number;
  source: Record<string, unknown>;
  values: Record<string, unknown>;
  status: 'ready' | 'created' | 'skipped' | 'rejected';
  reason: string;
  defaulted_fields: string[];
  account_id: number | null;
}
export function validImport(value: unknown): value is ImportSummary {
  const item = value as ImportSummary | null;
  if (
    !item ||
    typeof item.id !== 'string' ||
    !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(item.id) ||
    !validDefaults(item.defaults)
  )
    return false;
  return (
    typeof item.filename === 'string' &&
    typeof item.actor_email === 'string' &&
    typeof item.created_at === 'string' &&
    Number.isFinite(Date.parse(item.created_at)) &&
    (item.completed_at === null ||
      (typeof item.completed_at === 'string' &&
        Number.isFinite(Date.parse(item.completed_at)))) &&
    ['ready', 'completed', 'failed'].includes(item.status) &&
    typeof item.error === 'string' &&
    ['total', 'ready', 'created', 'skipped', 'rejected', 'defaulted'].every(
      (key) =>
        Number.isSafeInteger(item[key as keyof ImportSummary]) &&
        Number(item[key as keyof ImportSummary]) >= 0 &&
        Number(item[key as keyof ImportSummary]) <= 10000,
    ) &&
    item.ready + item.created + item.skipped + item.rejected === item.total &&
    item.defaulted <= item.total
  );
}
export function validImportRow(value: unknown): value is ImportRow {
  const row = value as ImportRow | null;
  return (
    !!row &&
    Number.isSafeInteger(row.row_number) &&
    row.row_number >= 2 &&
    row.row_number <= 10001 &&
    !!row.source &&
    typeof row.source === 'object' &&
    !Array.isArray(row.source) &&
    !!row.values &&
    typeof row.values === 'object' &&
    !Array.isArray(row.values) &&
    ['ready', 'created', 'skipped', 'rejected'].includes(row.status) &&
    typeof row.reason === 'string' &&
    Array.isArray(row.defaulted_fields) &&
    row.defaulted_fields.every((key) => IMPORT_COLUMNS.includes(key)) &&
    (row.account_id === null ||
      (Number.isSafeInteger(row.account_id) && row.account_id > 0))
  );
}
/** A successful HTTP response that cannot safely be downloaded as a file. */
export class ImportDownloadError extends Error {
  name = 'ImportDownloadError';
}

/** Validation guidance decoded only from the expected, bounded JSON refusal. */
class ImportDownloadRefusal extends Error {}

/** Decode only small JSON validation envelopes; never display server details. */
async function downloadRefusal(failure: unknown): Promise<string | undefined> {
  if (typeof failure !== 'object' || failure === null) return;
  const response = (failure as {
    response?: {
      status?: number;
      data?: unknown;
      headers?: Record<string, unknown>;
    };
  }).response;
  const blob = response?.data;
  const mediaType = String(response?.headers?.['content-type'] ?? '')
    .split(';')[0].trim().toLowerCase();
  if (
    response?.status !== 400 || mediaType !== 'application/json' ||
    !(blob instanceof Blob) || !blob.size || blob.size > 8192
  ) return;
  try {
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => { resolve(String(reader.result)); };
      reader.onerror = reject;
      reader.readAsText(blob);
    });
    const body: unknown = JSON.parse(text);
    if (
      !body || typeof body !== 'object' ||
      Object.getPrototypeOf(body) !== Object.prototype
    ) return;
    const validation = body as { code?: unknown; format?: unknown };
    if (validation.code !== 'invalid_request') return;
    const messages = typeof validation.format === 'string'
      ? [validation.format] : validation.format;
    if (
      !Array.isArray(messages) || !messages.length || messages.length > 5 ||
      !messages.every(message =>
        typeof message === 'string' && message.trim().length > 0 &&
        message.length <= 1024,
      )
    ) return;
    const message = messages.join(' ');
    if (message.length <= 1024) return message;
  } catch {
    // Unrecognised bodies retain the generic download error; never expose details.
  }
}

/** Select user guidance while retaining generic messages for untrusted failures. */
export function importError(
  failure: unknown,
  context: 'request' | 'download' = 'request',
): string {
  if (failure instanceof ImportDownloadRefusal) return failure.message;
  if (
    failure instanceof ImportDownloadError ||
    (failure as Error)?.name === 'InvalidDownload'
  )
    return 'The file could not be downloaded. Please try again.';
  const error = apiError(failure, 'Import results');
  if (!error) return '';
  if (context === 'download' && ![401, 403, 429].includes(error.status ?? 0))
    return 'The file could not be downloaded. Please try again.';
  if (error.status === 400)
    return (
      Object.entries(error.fields)
        .filter(([key]) => key !== 'code')
        .map(([, value]) => value)
        .join(' ') || 'Check the file and try again.'
    );
  if (error.status === 413)
    return 'Files are limited to 10 MB. Split the file and try again.';
  if (error.status === 409)
    return 'This import may already be running. Refresh its saved result before retrying.';
  if (error.status === 403)
    return 'You do not have permission to import customer accounts.';
  if (error.status === 401 || error.status === 429) return error.message;
  return 'The request could not be confirmed. Refresh the saved result before retrying. If uploading, retry the same file or check Import history.';
}
/** Keep filename validation inside the synchronous download cleanup scope. */
function downloadImportBlob(
  blob: Blob,
  format: 'csv' | 'xlsx',
  contentDisposition: string | undefined,
) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = downloadFilename(contentDisposition, format, 'AccountImport');
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }
}

/** Download an authenticated import file, or explain a refused XLSX correction. */
export async function downloadImportFile(
  path: string,
  format: 'csv' | 'xlsx',
  access: string | null,
  signal: AbortSignal,
) {
  if (!access) throw new Error('Sign in again to download an import file.');
  // Re-read mutable cancellation state after each asynchronous operation.
  const isAborted = () => signal.aborted;
  let response: AxiosResponse<Blob>;
  try {
    response = await api.get<Blob>(`${IMPORT_ENDPOINT}${path}`, {
      headers: { Authorization: `Bearer ${access}` },
      responseType: 'blob',
      timeout: 30000,
      signal,
    });
  } catch (failure) {
    if (isAborted()) return;
    const message = format === 'xlsx' && path.endsWith('/report/?mode=corrections')
      ? await downloadRefusal(failure) : undefined;
    if (isAborted()) return;
    if (message) throw new ImportDownloadRefusal(message);
    throw failure;
  }
  if (signal.aborted) return;
  const expected =
    format === 'csv'
      ? 'text/csv'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (
    !(response.data instanceof Blob) ||
    !response.data.size ||
    response.data.size > 64 * 1024 * 1024 ||
    String(response.headers['content-type']).split(';')[0] !== expected
  )
    throw new ImportDownloadError('Invalid import download');
  downloadImportBlob(response.data, format, response.headers['content-disposition']);
}
